// 新手引导（spec §4.6）。进度写在存档 onboarding.step：
//   0 → 第 1 局：手势动画教拖动；第一次粘连时暂停 1 秒讲解 → 1
//   1 → 第 2 局开局：高亮"原图参考"和"提示"，各一句 → 2
//   2 → 完成

export const MERGE_PAUSE_MS = 1000;

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => {
  const e = document.querySelector<T>(sel);
  if (!e) throw new Error(`Missing ${sel}`);
  return e;
};

export type OnboardStage = 'none' | 'drag' | 'merge' | 'tools' | 'intro';
export type IntroKind = 'rotate' | 'flip';

/** 新玩法第一次出现时的一步讲解（文字在这里统一管） */
export const INTRO_COPY: Record<IntroKind, { title: string; text: string; note: string }> = {
  rotate: { title: '新玩法：旋转块', text: '点一下旋转块，把它转正', note: '每点一下转 90°，算一步；转正了才会和邻居粘连' },
  flip: { title: '新玩法：背面块', text: '它旁边的块拼对了，它就会翻开', note: '放回它自己的位置也会翻开；背面朝上也能照常拖动' },
};

export class Onboarding {
  private readonly root = $('#onboard');
  private readonly hand = $('#onboard-hand');
  private readonly caption = $('#onboard-caption');
  private readonly tools = $('#onboard-tools');
  private readonly intro = $('#onboard-intro');
  stage: OnboardStage = 'none';
  /** 正在讲解的新玩法 */
  introKind: IntroKind | null = null;
  private mergeLeft = 0;
  private onMergeDone: (() => void) | null = null;

  /** 第 1 局：手从 from 拖到 to（屏幕 CSS px），循环播放，直到第一次走子 */
  showDrag(from: { x: number; y: number }, to: { x: number; y: number }): void {
    this.stage = 'drag';
    this.root.hidden = false;
    this.root.dataset.stage = 'drag';
    this.hand.hidden = false;
    this.hand.style.setProperty('--fx', `${from.x}px`);
    this.hand.style.setProperty('--fy', `${from.y}px`);
    this.hand.style.setProperty('--tx', `${to.x}px`);
    this.hand.style.setProperty('--ty', `${to.y}px`);
    this.caption.textContent = '按住一块拖到别处，两块会交换位置';
    this.caption.hidden = false;
    this.tools.hidden = true;
    this.intro.hidden = true;
  }

  /**
   * 新玩法讲解（只出现一次，存档 intros 记着）：压暗画面，圈出棋盘上的一个示例块，一张说明卡；点任意处收起。
   * tile = 示例块在屏幕上的格子（CSS px）；说明卡放在示例块的另一半屏幕，不挡住它。
   */
  showIntro(kind: IntroKind, tile: { x: number; y: number; size: number } | null, done: () => void): void {
    this.stage = 'intro';
    this.introKind = kind;
    this.root.hidden = false;
    this.root.dataset.stage = 'intro';
    this.hand.hidden = true;
    this.caption.hidden = true;
    this.tools.hidden = true;
    this.intro.hidden = false;
    this.intro.dataset.kind = kind;
    const copy = INTRO_COPY[kind];
    $('#intro-title').textContent = copy.title;
    $('#intro-text').textContent = copy.text;
    $('#intro-note').textContent = copy.note;
    const ring = $('#intro-ring');
    const card = $('#intro-card');
    ring.hidden = !tile;
    if (tile) {
      const pad = 2;
      ring.style.left = `${tile.x - tile.size / 2 - pad}px`;
      ring.style.top = `${tile.y - tile.size / 2 - pad}px`;
      ring.style.width = ring.style.height = `${tile.size + pad * 2}px`;
      ring.style.borderRadius = `${Math.max(6, tile.size * 0.18)}px`;
    }
    // 默认在棋盘上方；示例块离顶部太近（会被卡片挡住）时放到底部
    card.classList.remove('is-bottom');
    const cardBottom = card.getBoundingClientRect().bottom;
    card.classList.toggle('is-bottom', !!tile && tile.y - tile.size / 2 - 8 < cardBottom);
    this.root.onclick = () => {
      this.root.onclick = null;
      this.hide();
      done();
    };
  }

  /** 第一次走子后收起手势 */
  dragDone(): void {
    if (this.stage !== 'drag') return;
    this.hide();
  }

  /** 第一次粘连：讲解 1 秒（期间棋盘锁定、计时暂停），之后回调 */
  showMerge(done: () => void): void {
    this.stage = 'merge';
    this.root.hidden = false;
    this.root.dataset.stage = 'merge';
    this.hand.hidden = true;
    this.tools.hidden = true;
    this.intro.hidden = true;
    this.caption.textContent = '拼对的块会粘在一起，一起移动';
    this.caption.hidden = false;
    this.mergeLeft = MERGE_PAUSE_MS;
    this.onMergeDone = done;
  }

  /** 第 2 局：高亮两个道具按钮，点任意处收起 */
  showTools(done: () => void): void {
    this.stage = 'tools';
    this.root.hidden = false;
    this.root.dataset.stage = 'tools';
    this.hand.hidden = true;
    this.caption.hidden = true;
    this.intro.hidden = true;
    this.tools.hidden = false;
    // 两个按钮挨着，两句说明上下错开，避免重叠
    const place = (btnSel: string, tipSel: string, lift: number) => {
      const r = $(btnSel).getBoundingClientRect();
      const tip = $(tipSel);
      // 居中于按钮，但整句留在屏幕内（tip 用 translate(-50%) 定位）
      const half = tip.offsetWidth / 2;
      const cx = Math.max(half + 8, Math.min(window.innerWidth - half - 8, r.left + r.width / 2));
      tip.style.left = `${cx}px`;
      tip.style.top = `${r.top - 12 - lift}px`;
      const ring = $(`${tipSel}-ring`);
      ring.style.left = `${r.left - 6}px`;
      ring.style.top = `${r.top - 6}px`;
      ring.style.width = `${r.width + 12}px`;
      ring.style.height = `${r.height + 12}px`;
    };
    place('#btn-preview', '#tip-preview', 44);
    place('#btn-hint', '#tip-hint', 0);
    this.root.onclick = () => {
      this.root.onclick = null;
      this.hide();
      done();
    };
  }

  /** 引导讲解是否正在暂停对局（计时不计入） */
  get blocking(): boolean {
    return this.stage === 'merge' || this.stage === 'tools' || this.stage === 'intro';
  }

  update(dtMs: number): void {
    if (this.stage !== 'merge') return;
    this.mergeLeft -= dtMs;
    if (this.mergeLeft <= 0) {
      const cb = this.onMergeDone;
      this.onMergeDone = null;
      this.hide();
      cb?.();
    }
  }

  hide(): void {
    this.stage = 'none';
    this.introKind = null;
    this.intro.hidden = true;
    this.root.hidden = true;
    this.root.onclick = null;
    delete this.root.dataset.stage;
  }
}
